import { useState } from 'react';
import { ApiError, api } from '../../lib/api';
import type { Territory } from '../../lib/types';
import type { AccountTab } from '../../lib/url';
import { Field, SettingsCard, TextInput } from '@allrouted/ui/Field';
import { Select } from '@allrouted/ui/Select';
import { useUi } from '@allrouted/ui/Toaster';
import { useAuth } from '../AuthGate';
import { PeoplePage } from './PeoplePage';
import { ApiKeysPage } from './ApiKeysPage';
import { ProfilePage } from './ProfilePage';

/**
 * Users and API keys: who can get in, and what can call the API.
 *
 * Three pages under one heading. People and API keys are for owners and
 * admins to manage; everybody has their own profile. How addresses are
 * geocoded and routes are solved is not here: that is run by AllRouted for
 * every account, from the platform admin panel.
 */
export function AccountView({
  territories,
  tab,
  onTab,
}: {
  territories: Territory[];
  tab: AccountTab;
  onTab: (tab: AccountTab) => void;
}) {
  const { me } = useAuth();
  const canManage = me.user?.role === 'owner' || me.user?.role === 'admin';
  /* A key list a dispatcher cannot load is not a page worth offering them. */
  const tabs: { id: AccountTab; label: string }[] = [
    { id: 'people', label: 'People' },
    ...(canManage ? [{ id: 'api-keys' as const, label: 'API keys' }] : []),
    { id: 'profile', label: 'Your profile' },
  ];
  const current = tabs.some((t) => t.id === tab) ? tab : 'people';

  return (
    <div className="account-page">
      <header className="account-head">
        <div>
          <h1>Users and API keys</h1>
          <p>{me.account.name}</p>
        </div>
        <div className="tabs" role="tablist">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              className="tab"
              aria-selected={current === t.id}
              onClick={() => onTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </header>

      {current === 'people' ? <PeoplePage /> : null}
      {current === 'api-keys' && canManage ? <ApiKeysPage territories={territories} /> : null}
      {current === 'profile' ? (
        <>
          <ProfilePage />
          {canManage ? <FormatsCard /> : null}
        </>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Shared bits
 * ------------------------------------------------------------------ */

/** "3 minutes ago", "yesterday", "12 Mar": how long ago, at a glance. The full time is in the title. */
export function ago(iso: string | null): string {
  if (!iso) return 'Never';
  const then = new Date(iso).getTime();
  const s = Math.round((Date.now() - then) / 1000);
  if (s < 0) return until(iso);
  if (s < 60) return 'Just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  if (s < 2 * 86400) return 'Yesterday';
  if (s < 30 * 86400) return `${Math.floor(s / 86400)} days ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** "in 3 days", for an expiry. */
export function until(iso: string): string {
  const s = Math.round((new Date(iso).getTime() - Date.now()) / 1000);
  if (s <= 0) return 'Expired';
  if (s < 3600) return `In ${Math.max(1, Math.floor(s / 60))} min`;
  if (s < 86400) return `In ${Math.floor(s / 3600)} h`;
  if (s < 60 * 86400) return `In ${Math.floor(s / 86400)} days`;
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export const fullTime = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : undefined);

export const errText = (err: unknown) => (err instanceof ApiError ? err.message : 'Something went wrong. Try again.');

/** A value shown once, with a copy button: a new key or a generated password. */
export function Reveal({
  title,
  body,
  value,
  onDone,
}: {
  title: string;
  body: string;
  value: string;
  onDone?: () => void;
}) {
  const ui = useUi();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      ui.notify('info', 'Select and copy it', 'The browser would not give us the clipboard.');
    }
  };
  return (
    <div className="key-reveal">
      <strong>{title}</strong>
      <p>{body}</p>
      <div className="key-value">
        <code>{value}</code>
        <button type="button" className="btn" onClick={() => void copy()}>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      {onDone ? (
        <button type="button" className="btn" onClick={onDone}>
          Done
        </button>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Language and formats
 * ------------------------------------------------------------------ */

/**
 * The incumbent keeps these in Settings › Language & Formats, at account
 * scope: a distance unit is how this operation reads numbers, not a property
 * of one metro. Only the two that cannot be inferred are stored; dates and
 * times render through `Intl` from the browser's own locale.
 */
function FormatsCard() {
  const ui = useUi();
  const { me, refresh } = useAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({
    distanceUnit: me.account.distance_unit,
    currency: me.account.currency,
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.updateFormats(draft);
      /* Re-read the session: every screen that renders a distance or a price reads it. */
      await refresh();
      setEditing(false);
      ui.notify('ok', 'Formats saved', 'Distances and prices now read this way everywhere.');
    } catch (err) {
      ui.notify('error', 'Could not save', errText(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsCard
      title="Language & formats"
      description="For everybody on this account. Stored values do not change: distances stay in metres and money stays a plain amount."
      editing={editing}
      saving={saving}
      onEdit={() => {
        setDraft({ distanceUnit: me.account.distance_unit, currency: me.account.currency });
        setEditing(true);
      }}
      onCancel={() => setEditing(false)}
      onSave={() => void save()}
    >
      <Field label="Distance unit">
        <Select
          value={draft.distanceUnit}
          options={[
            { value: 'mi', label: 'Miles' },
            { value: 'km', label: 'Kilometres' },
          ]}
          onChange={(v) => setDraft({ ...draft, distanceUnit: v as 'mi' | 'km' })}
        />
      </Field>
      <Field label="Currency" hint="ISO code, e.g. USD">
        <TextInput
          value={draft.currency}
          onChange={(v) => setDraft({ ...draft, currency: v.toUpperCase().slice(0, 3) })}
        />
      </Field>
    </SettingsCard>
  );
}
